import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Clock, MapPin, Phone, ShieldCheck, Truck, Wrench } from "lucide-react";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Footer } from "@/components/Footer";
import { POINTS, CITIES } from "@/pages/CityPage";

const BRANDS = {
  iphone: { name: "iPhone" },
  samsung: { name: "Samsung" },
};

const REPAIRS = {
  "ecran": {
    titleBase: "Réparation écran cassé",
    titleBrand: "Réparation écran {brand}",
    detail:
      "Vitre et dalle remplacées, tactile et couleurs d'origine, joint d'étanchéité refait au remontage : votre écran est changé en 1 heure environ, sous vos yeux, avec une pièce premium garantie 3 mois.",
    intro:
      "Écran fissuré, tactile qui ne répond plus, taches noires ou lignes qui apparaissent : un technicien SLH PHONE se déplace à votre domicile ou à votre bureau à Lyon et dans toute la métropole pour remplacer votre écran sur place, en 1 heure environ. Vitre et dalle remplacées, tactile et couleurs d'origine, joint d'étanchéité refait au remontage.",
    brands: {
      iphone:
        "Sur iPhone — de l'iPhone 8 aux derniers modèles — nous installons des dalles premium respectant les couleurs et la luminosité d'origine. Face ID, capteur de proximité et luminosité automatique sont conservés après la réparation.",
      samsung:
        "Sur Samsung Galaxy (S, A, Z Flip, Z Fold), nous remplaçons la dalle AMOLED complète pour retrouver des couleurs identiques à l'origine. Le lecteur d'empreinte sous l'écran et le tactile sont testés avec vous avant notre départ.",
    },
  },
  "batterie": {
    titleBase: "Remplacement batterie",
    titleBrand: "Remplacement batterie {brand}",
    detail:
      "Autonomie en chute libre, batterie gonflée, coupures intempestives : nous installons une batterie neuve à 100 % de capacité en 30 à 45 minutes, garantie 3 mois.",
    intro:
      "Votre téléphone se décharge en quelques heures, s'éteint à 20 % ou la batterie gonfle ? Un technicien SLH PHONE vient la remplacer chez vous ou à votre bureau à Lyon et dans toute la métropole. Intervention de 30 à 45 minutes, batterie neuve à 100 % de capacité, ancienne batterie recyclée.",
    brands: {
      iphone:
        "Sur iPhone, nous installons une batterie premium à capacité d'origine. Vous repartez avec une autonomie comme au premier jour et le message « Service » lié à l'usure de la batterie disparaît dès le remplacement.",
      samsung:
        "Sur Samsung Galaxy, la batterie neuve installée conserve la charge rapide adaptative. Nous vérifions ensemble l'état de charge et la tenue en veille avant de repartir.",
    },
  },
  "connecteur-charge": {
    titleBase: "Réparation connecteur de charge",
    titleBrand: "Réparation connecteur de charge {brand}",
    intro:
      "Le câble ne tient plus, la charge se coupe au moindre mouvement ou ne démarre qu'en forçant ? Un technicien SLH PHONE intervient à votre domicile ou à votre bureau à Lyon et alentours. Souvent, un nettoyage soigneux du connecteur suffit ; sinon, la nappe de charge est remplacée sur place.",
    brands: {
      iphone:
        "Sur iPhone (Lightning ou USB-C selon le modèle), nous testons plusieurs câbles après l'intervention pour garantir une charge stable, y compris avec la charge rapide.",
      samsung:
        "Sur Samsung Galaxy (USB-C), la charge rapide et le transfert de données sont vérifiés après le nettoyage ou le remplacement de la nappe de charge.",
    },
  },
  "camera": {
    titleBase: "Réparation caméra",
    titleBrand: "Réparation caméra {brand}",
    intro:
      "Photos floues, optique rayée, application appareil photo qui plante ou écran noir à l'ouverture ? Un technicien SLH PHONE remplace le module caméra avant ou arrière directement chez vous ou à votre bureau à Lyon et dans toute la métropole, en 1 heure environ.",
    brands: {
      iphone:
        "Sur iPhone, les modules photo et les capteurs Face ID sont manipulés avec la plus grande précaution. Mise au point, modes portrait et stabilisation sont testés après la réparation.",
      samsung:
        "Sur Samsung Galaxy, nous remplaçons le module défaillant — grand-angle, ultra grand-angle ou zoom — et vérifions la netteté sur chaque capteur avec vous.",
    },
  },
  "desoxydation": {
    titleBase: "Désoxydation téléphone",
    titleBrand: "Désoxydation {brand}",
    intro:
      "Téléphone tombé dans l'eau, dans les toilettes ou sous la pluie ? Éteignez-le immédiatement et ne le chargez pas. Un technicien SLH PHONE intervient rapidement à votre domicile à Lyon et alentours : démontage complet, nettoyage à l'alcool isopropylique et traitement de la corrosion. Plus l'intervention est rapide, plus les chances de sauver l'appareil sont élevées.",
    brands: {
      iphone:
        "Sur iPhone, même les modèles annoncés « résistants à l'eau » finissent par laisser passer l'humidité quand les joints vieillissent. Nous désoxydons la carte mère et remplaçons les nappes endommagées si nécessaire.",
      samsung:
        "Sur Samsung Galaxy, nous traitons la carte mère et le connecteur de charge, premières victimes de l'oxydation, puis testons chaque fonction avant de vous rendre l'appareil.",
    },
  },
  "logiciel": {
    titleBase: "Dépannage logiciel téléphone",
    titleBrand: "Dépannage logiciel {brand}",
    intro:
      "Téléphone bloqué au démarrage, applications qui plantent, sauvegarde et transfert de données vers un nouvel appareil, remise à zéro ou nettoyage de publicités intempestives : un technicien SLH PHONE règle vos problèmes logiciels à domicile ou au bureau à Lyon et dans toute la métropole.",
    brands: {
      iphone:
        "Sur iPhone : restauration iOS, blocage sur la pomme, transfert complet vers votre nouvel iPhone, récupération de photos et de contacts.",
      samsung:
        "Sur Samsung Galaxy : restauration Android, transfert Smart Switch vers votre nouveau Galaxy, suppression des applications parasites et optimisation du stockage.",
    },
  },
};

export const REPAIR_SLUGS = Object.keys(REPAIRS);
export const BRAND_SLUGS = Object.keys(BRANDS);
export const COMBO_REPAIRS = ["ecran", "batterie"];

export const RepairPage = ({ repairSlug, brandSlug, citySlug }) => {
  const repair = REPAIRS[repairSlug];
  const brand = brandSlug ? BRANDS[brandSlug] : null;
  const city = citySlug ? CITIES[citySlug] : null;

  const title = repair
    ? (brand
        ? repair.titleBrand.replace("{brand}", brand.name)
        : repair.titleBase)
    : null;
  const place = city ? city.name : "Lyon";

  useEffect(() => {
    if (!repair) return;
    document.title = `${title} à domicile à ${place} | SLH PHONE`;
    const meta = document.querySelector('meta[name="description"]');
    meta?.setAttribute(
      "content",
      `${title} à domicile à ${place} et alentours : intervention en 1h, déplacement offert, garantie 3 mois. Disponible de 10h à 23h, 7j/7. Devis gratuit au 06 51 48 74 03.`
    );
    const canonical = document.querySelector('link[rel="canonical"]');
    canonical?.setAttribute(
      "href",
      `https://slh-phone.fr${window.location.pathname.replace(/\/+$/, "")}`
    );
    return () => {
      document.title = "Réparation téléphone à domicile à Lyon | SLH PHONE";
      meta?.setAttribute(
        "content",
        "Réparation à domicile ou au bureau iPhone & Samsung à Lyon et les alentours. Écran cassé, batterie, caméra... Pièces premium, garantie 3 mois. Intervention rapide de 10h à 23h, 7j/7."
      );
      canonical?.setAttribute("href", "https://slh-phone.fr/");
    };
  }, [repair, brand, city, title, place]);

  if (!repair || (brandSlug && !brand) || (citySlug && !city)) {
    return <Navigate to="/" replace />;
  }

  const otherRepairs = REPAIR_SLUGS.filter((s) => s !== repairSlug);

  return (
    <div className="relative min-h-screen bg-[#E8F1FC] text-zinc-900 antialiased" data-testid="repair-page">
      <header className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-[80px] flex items-center justify-between">
          <Link to="/" data-testid="repair-back-home" aria-label="Retour à l'accueil SLH PHONE">
            <img src="/assets/logo.png" alt="Logo SLH PHONE" className="h-12 w-auto mix-blend-screen" />
          </Link>
          <a
            href="tel:+33651487403"
            className="flex items-center gap-2 rounded-full bg-[#0055FF] px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-white shadow-[0_0_20px_rgba(0,85,255,0.35)] hover:shadow-[0_0_30px_rgba(0,85,255,0.55)] transition-shadow duration-300"
            data-testid="repair-phone-cta"
          >
            <Phone className="h-3 w-3" />
            06 51 48 74 03
          </a>
        </div>
      </header>

      <main className="pt-[80px]">
        <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-[#0055FF] transition-colors duration-300 mb-10"
            data-testid="repair-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
          </Link>

          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.95] text-[clamp(2rem,6vw,5rem)] text-zinc-950">
            {title}
            <br />
            à domicile à <span className="text-[#0055FF]">{place}</span>
          </h1>

          <div className="blue-beam w-full max-w-md mt-8" aria-hidden="true" />

          {city && (
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed font-light text-zinc-600" data-testid="repair-city-intro">
              {city.intro}
            </p>
          )}

          <p className={`${city ? "mt-5" : "mt-8"} max-w-2xl text-base md:text-lg leading-relaxed font-light text-zinc-600`} data-testid="repair-intro">
            {city && repair.detail ? repair.detail : repair.intro}
          </p>

          {brand && (
            <p className="mt-5 max-w-2xl text-base md:text-lg leading-relaxed font-light text-zinc-600" data-testid="repair-brand-intro">
              {repair.brands[brandSlug]}
            </p>
          )}

          <div className="mt-10 flex flex-wrap items-center gap-5">
            <a
              href="/reservation"
              className="flex items-center gap-3 rounded-full bg-[#25D366] px-9 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-white shadow-[0_0_24px_rgba(37,211,102,0.4)] hover:shadow-[0_0_36px_rgba(37,211,102,0.6)] transition-shadow duration-300"
              data-testid="repair-whatsapp-cta"
            >
              Réserver
            </a>
            <a
              href="tel:+33651487403"
              className="rounded-full border border-zinc-300 px-9 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-zinc-600 hover:border-zinc-900 hover:text-zinc-950 transition-colors duration-300"
              data-testid="repair-call-cta"
            >
              06 51 48 74 03
            </a>
          </div>

          {!brand && !city && (
            <p className="mt-10 text-sm font-light text-zinc-600" data-testid="repair-brand-links">
              Vous avez un{" "}
              <Link to={`/reparation-${repairSlug}-iphone-lyon`} className="text-[#0055FF] hover:underline" data-testid="repair-link-iphone">
                iPhone
              </Link>{" "}
              ou un{" "}
              <Link to={`/reparation-${repairSlug}-samsung-lyon`} className="text-[#0055FF] hover:underline" data-testid="repair-link-samsung">
                Samsung
              </Link>{" "}
              ? Consultez la page dédiée à votre marque.
            </p>
          )}
        </section>

        <section className="bg-white border-y border-zinc-200 py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight uppercase text-zinc-950 mb-12">
              Pourquoi choisir SLH PHONE ?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {POINTS.map((point, i) => {
                const Icon = [Truck, Wrench, ShieldCheck, MapPin, ShieldCheck, Clock][i % 6];
                return (
                  <div
                    key={i}
                    className="flex items-start gap-4 border border-zinc-200 bg-[#F6FAFF] p-6"
                    data-testid={`repair-point-${i}`}
                  >
                    <Icon className="h-6 w-6 shrink-0 text-[#0055FF] mt-0.5" />
                    <p className="text-sm leading-relaxed text-zinc-700">{point}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight uppercase text-zinc-950 mb-8">
            Nos autres réparations à domicile
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="repair-related">
            {otherRepairs.map((slug) => (
              <li key={slug}>
                <Link
                  to={`/reparation-${slug}-lyon`}
                  className="block border border-zinc-200 bg-white px-6 py-4 text-sm font-medium text-zinc-700 hover:border-[#0055FF]/60 hover:bg-[#0055FF]/5 hover:text-zinc-950 transition-colors duration-300"
                  data-testid={`repair-related-${slug}`}
                >
                  {REPAIRS[slug].titleBase} à Lyon
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-sm font-light text-zinc-600">
            Nous intervenons aussi à{" "}
            <Link to="/reparation-telephone-villeurbanne" className="text-[#0055FF] hover:underline">Villeurbanne</Link>,{" "}
            <Link to="/reparation-telephone-saint-priest" className="text-[#0055FF] hover:underline">Saint-Priest</Link>,{" "}
            <Link to="/reparation-telephone-venissieux" className="text-[#0055FF] hover:underline">Vénissieux</Link>{" "}
            et dans{" "}
            <Link to="/#zones" className="text-[#0055FF] hover:underline">toute la métropole de Lyon</Link>.
          </p>
        </section>
      </main>

      <Footer onNavigate={(id) => (window.location.href = "/" + id)} />
      <WhatsAppButton />
    </div>
  );
};
